import { ArrowLeftOutlined, CopyOutlined, CreditCardOutlined, LockOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import StatsCard from '../../../components/Cards/StartsCard';
import { Card, CardHead, CardTitle, Hint, Segmented } from '../../../components/Layout/PagePieces';
import PageLayout from '../../../components/Layout/PageLayout';
import { ButtonLink } from '../../../components/Link/Link';
import Page from '../../../components/Page/Page';
import Table from '../../../components/Table/Table';
import CustomerLinkCell from '../../../components/Table/TableAccessories/CustomerLinkCell';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableMoneyFormatter from '../../../components/Table/TableAccessories/TableMoneyFormatter';
import { customerPath } from '../../../context/InboxContext';
import useCustomerProfile from '../../../hooks/dashboard/risk/useCustomerProfile';
import { useGet } from '../../../hooks/useApi';
import { RISK_RULES, isActiveStatus } from '../../../models/risk/risk.model';
import { LIMITS, RISK_CENTER } from '../../../routes/routeConstants';
import {
  capitalize,
  formatNaira,
  formatNumber,
  formatPhone,
  tableFormatDateTimeNG,
  timeAgo,
  toChatId,
} from '../../../utils/utils';
import GiftDetailSlider from '../Gifts/GiftDetailSlider';
import ScheduleDetailSlider from '../Schedules/ScheduleDetailSlider';
import TransactionDetailSlider from '../Transactions/TransactionDetailSlider';
import ChatAllowanceCard from './ChatAllowanceCard';

type Tab = 'purchases' | 'gifts' | 'schedules';

const naira = (v?: string | number | null) => formatNaira(Number(v ?? 0));
const words = (v: string) => capitalize(String(v).replace(/_/g, ' ').toLowerCase());

// Everything about one customer on one page (Stripe's customer view): who they
// are, how they pay, what they've bought, gifts, schedules, risk and the
// accounts linked to them. Reached from ⌘K (type a phone number) or any
// "Customer" link.
const Customer = () => {
  const { chatId: raw = '' } = useParams();
  const chatId = toChatId(raw);
  const navigate = useNavigate();
  const user = useGet<{ id: string; chat_id: string }>('No customer with that number');
  const { profile, loading } = useCustomerProfile(user.data?.id);
  const [tab, setTab] = useState<Tab>('purchases');
  // True once the lookup has run, so "not found" never flashes before it.
  const [looked, setLooked] = useState(false);

  useEffect(() => {
    setLooked(false);
    user.setData(null);
    user.fetch(`api/v1/admin/users/${chatId}`);
    // eslint-disable-next-line
  }, [chatId]);

  useEffect(() => {
    if (user.loading) setLooked(true);
  }, [user.loading]);

  const notFound = looked && !user.loading && !user.data;
  const name = profile?.display_name || formatPhone(chatId);
  const activeAlerts = (profile?.alerts ?? []).filter((a) => isActiveStatus(a.status));

  const copy = () => {
    navigator.clipboard?.writeText(formatPhone(chatId).replace(/\s/g, '')).then(
      () => toast.success('Number copied'),
      () => toast.error('Couldn’t copy')
    );
  };

  return (
    <Page title={`Hethera | ${name}`}>
      <PageLayout>
        <Back type='button' onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Back
        </Back>

        {notFound ? (
          <Card>
            <CardTitle>No customer with the number {formatPhone(chatId)}</CardTitle>
            <Hint>Check the number, or search for it with ⌘K. Customers appear here once they’ve messaged Hethera.</Hint>
          </Card>
        ) : (
          <>
            <Header>
              <Avatar aria-hidden>{(profile?.display_name || '#').slice(0, 1).toUpperCase()}</Avatar>
              <div className='who'>
                <h1>{loading && !profile ? 'Loading…' : name}</h1>
                <div className='line'>
                  <span>{formatPhone(chatId)}</span>
                  <IconBtn type='button' onClick={copy} aria-label='Copy number' title='Copy number'>
                    <CopyOutlined />
                  </IconBtn>
                  {profile?.email && <span>· {profile.email}</span>}
                  {profile && <span>· Joined {tableFormatDateTimeNG(profile.created_at)}</span>}
                </div>
                {profile && (
                  <div className='chips'>
                    <Chip className={profile.onboarding_completed_at ? 'ok' : 'warn'}>
                      {profile.onboarding_completed_at ? 'Set up and can pay' : 'Hasn’t finished set-up'}
                    </Chip>
                    {!profile.is_active && <Chip className='bad'>Account disabled</Chip>}
                    {profile.pin_locked_until && new Date(profile.pin_locked_until) > new Date() && (
                      <Chip className='bad'>PIN locked</Chip>
                    )}
                    <Chip className={profile.risk_score >= 50 ? 'bad' : profile.risk_score > 0 ? 'warn' : 'ok'}>
                      Risk score {profile.risk_score}
                    </Chip>
                  </div>
                )}
              </div>
              <div className='actions'>
                <ButtonLink to={`/${LIMITS}`} className='ghost'>
                  Limits
                </ButtonLink>
              </div>
            </Header>

            {profile && (
              <>
                <Grid4>
                  <StatsCard.Root>
                    <StatsCard.Header hint='The total of their successful purchases.'>Total spent</StatsCard.Header>
                    <StatsCard.Value>{naira(profile.stats.total_spent)}</StatsCard.Value>
                    <StatsCard.PlainFooter>Average {naira(profile.stats.average_amount)}</StatsCard.PlainFooter>
                  </StatsCard.Root>
                  <StatsCard.Root>
                    <StatsCard.Header>Purchases</StatsCard.Header>
                    <StatsCard.Value>{formatNumber(profile.stats.successful)}</StatsCard.Value>
                    <StatsCard.PlainFooter>
                      of {formatNumber(profile.stats.total_transactions)} tried
                      {profile.stats.declined ? ` · ${profile.stats.declined} declined` : ''}
                      {profile.stats.unresolved ? ` · ${profile.stats.unresolved} unconfirmed` : ''}
                    </StatsCard.PlainFooter>
                  </StatsCard.Root>
                  <StatsCard.Root>
                    <StatsCard.Header>Last purchase</StatsCard.Header>
                    <StatsCard.Value>
                      {profile.stats.last_transaction_at ? timeAgo(profile.stats.last_transaction_at) : 'Never'}
                    </StatsCard.Value>
                    <StatsCard.PlainFooter>
                      {profile.stats.distinct_recipients} recipient{profile.stats.distinct_recipients === 1 ? '' : 's'} paid
                    </StatsCard.PlainFooter>
                  </StatsCard.Root>
                  <StatsCard.Root>
                    <StatsCard.Header hint='Ejor: someone pays a bill for someone else. “Asked” = they asked others to pay; “paid” = they paid for others.'>
                      Ejor
                    </StatsCard.Header>
                    <StatsCard.Value>{formatNumber(profile.stats.ejors_paid)}</StatsCard.Value>
                    <StatsCard.PlainFooter>paid for others · asked {profile.stats.ejors_initiated}</StatsCard.PlainFooter>
                  </StatsCard.Root>
                </Grid4>

                <TwoUp>
                  <Card>
                    <CardHead>
                      <CardTitle>How they pay</CardTitle>
                    </CardHead>
                    {profile.card ? (
                      <Facts>
                        <li>
                          <CreditCardOutlined />
                          <span>
                            {profile.card.bank} {words(profile.card.card_type)} •••• {profile.card.last4}
                          </span>
                        </li>
                        <li>
                          <span className='k'>Added</span>
                          <span>{tableFormatDateTimeNG(profile.card.added_at)}</span>
                        </li>
                        <li>
                          <span className='k'>Card email</span>
                          <span>{profile.card.email}</span>
                        </li>
                      </Facts>
                    ) : (
                      <Hint>No card saved yet.</Hint>
                    )}
                    <Facts>
                      <li>
                        <LockOutlined />
                        <span>
                          {profile.pin_failed_attempts} wrong PIN attempt{profile.pin_failed_attempts === 1 ? '' : 's'} ·{' '}
                          {profile.pin_lockout_count} lockout{profile.pin_lockout_count === 1 ? '' : 's'}
                        </span>
                      </li>
                    </Facts>
                  </Card>

                  <Card>
                    <CardHead>
                      <CardTitle>Risk</CardTitle>
                      <ButtonLink to={`/${RISK_CENTER}?search=${chatId}`} className='more'>
                        Risk Center →
                      </ButtonLink>
                    </CardHead>
                    {activeAlerts.length === 0 ? (
                      <Hint>No open alerts.</Hint>
                    ) : (
                      <Alerts>
                        {activeAlerts.map((a) => (
                          <li key={a.id}>
                            <StatusCell statusKey='severity' data={{ severity: a.severity.toLowerCase() }} />
                            <span>{a.title || RISK_RULES[a.rule]}</span>
                          </li>
                        ))}
                      </Alerts>
                    )}
                    {profile.linked_accounts.length > 0 && (
                      <>
                        <Hint>Linked accounts</Hint>
                        <Alerts>
                          {profile.linked_accounts.map((l) => (
                            <li key={l.user_id}>
                              <ButtonLink to={customerPath(l.chat_id)} className='more'>
                                {l.display_name || formatPhone(l.chat_id)}
                              </ButtonLink>
                              <span className='why'>{l.link}</span>
                            </li>
                          ))}
                        </Alerts>
                      </>
                    )}
                  </Card>
                </TwoUp>

                <ChatAllowanceCard userId={profile.user_id} />

                <Section>
                  <Segmented role='tablist' aria-label='History'>
                    {(['purchases', 'gifts', 'schedules'] as Tab[]).map((t) => (
                      <button
                        key={t}
                        type='button'
                        role='tab'
                        aria-selected={tab === t}
                        className={tab === t ? 'active' : ''}
                        onClick={() => setTab(t)}
                      >
                        {t === 'purchases' ? 'Purchases' : t === 'gifts' ? 'Dash gifts' : 'Scheduled payments'}
                      </button>
                    ))}
                  </Segmented>

                  {tab === 'purchases' && (
                    <Table
                      key={`p-${profile.user_id}`}
                      tableHeader={{
                        Reference: 'reference',
                        Category: 'type',
                        Provider: 'provider',
                        Amount: 'amount',
                        Status: 'status',
                        Date: 'created_at',
                      }}
                      tableDataSourceUrl={`api/v1/admin/transactions?user_id=${profile.user_id}`}
                      size={10}
                      border
                      cellsCustomComp={{
                        type: <WordsCell dataKey='type' />,
                        amount: <TableMoneyFormatter dataKey='amount' />,
                        status: <StatusCell statusKey='status' hasDot positiveType />,
                        created_at: <TableDateTimeFormatter dataKey='created_at' />,
                      }}
                      clickRowShowSliderComp={(row) => <TransactionDetailSlider data={row as any} />}
                      hasClickableRow
                      emptyStateMsg='No purchases yet'
                    />
                  )}
                  {tab === 'gifts' && (
                    <Table
                      key={`g-${chatId}`}
                      tableHeader={{
                        Code: 'code',
                        From: 'sender_chat_id',
                        To: 'recipient_chat_id',
                        Amount: 'amount',
                        Status: 'status',
                        Created: 'created_at',
                      }}
                      tableDataSourceUrl={`api/v1/admin/gifts?search=${chatId}`}
                      size={10}
                      border
                      cellsCustomComp={{
                        sender_chat_id: <CustomerLinkCell dataKey='sender_chat_id' />,
                        recipient_chat_id: <CustomerLinkCell dataKey='recipient_chat_id' />,
                        amount: <TableMoneyFormatter dataKey='amount' />,
                        status: <StatusCell statusKey='status' hasDot />,
                        created_at: <TableDateTimeFormatter dataKey='created_at' />,
                      }}
                      clickRowShowSliderComp={(row) => <GiftDetailSlider data={row as any} />}
                      hasClickableRow
                      emptyStateMsg='No gifts sent or received'
                    />
                  )}
                  {tab === 'schedules' && (
                    <Table
                      key={`s-${profile.user_id}`}
                      tableHeader={{
                        Code: 'code',
                        Name: 'name',
                        Bill: 'bill_type',
                        Amount: 'amount',
                        'Next payment': 'next_run_date',
                        Status: 'status',
                      }}
                      tableDataSourceUrl={`api/v1/admin/schedules?user_id=${profile.user_id}`}
                      size={10}
                      border
                      cellsCustomComp={{
                        bill_type: <WordsCell dataKey='bill_type' />,
                        amount: <TableMoneyFormatter dataKey='amount' />,
                        status: <StatusCell statusKey='status' hasDot />,
                      }}
                      clickRowShowSliderComp={(row) => <ScheduleDetailSlider data={row as any} />}
                      hasClickableRow
                      emptyStateMsg='No scheduled payments'
                    />
                  )}
                </Section>
              </>
            )}
          </>
        )}
      </PageLayout>
    </Page>
  );
};

export default Customer;

const WordsCell: React.FC<{ data?: Record<string, any>; dataKey: string }> = ({ data, dataKey }) => (
  <>{data?.[dataKey] ? (data[dataKey] === 'GIFT' ? 'Dash gift' : words(data[dataKey])) : '—'}</>
);

const Back = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 0;
  border: 0;
  background: none;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;

  &:hover {
    color: var(--ink);
  }
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;

  & .who {
    flex: 1;
    min-width: 240px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  & h1 {
    margin: 0;
    font-size: clamp(24px, 2.4vw, 30px);
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: var(--ink);
  }
  & .line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }
  & .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
  }
  & .actions a {
    display: inline-flex;
    align-items: center;
    height: 34px;
    padding: 0 14px;
    border-radius: 9px;
    border: 1px solid var(--line);
    background: #ffffff;
    font-size: 13px;
    color: var(--ink);
  }
`;

const Avatar = styled.div`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
`;

const IconBtn = styled.button`
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: none;
  color: #7a8279;
  cursor: pointer;

  &:hover {
    background: var(--hover);
    color: var(--ink);
  }
`;

const Chip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;

  &.ok {
    background: rgba(31, 122, 69, 0.08);
    color: #1f7a45;
  }
  &.warn {
    background: rgba(217, 162, 27, 0.14);
    color: #8a5a00;
  }
  &.bad {
    background: rgba(217, 45, 32, 0.08);
    color: var(--red);
  }
`;

const Grid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;

  & .more {
    font-size: 13px;
    color: var(--ok);
  }
`;

const Facts = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: var(--ink-2);

  & li {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  & .k {
    width: 90px;
    flex: none;
    color: #7a8279;
    font-size: 13px;
  }
`;

const Alerts = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;

  & li {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  & .why {
    font-size: 12.5px;
    color: #7a8279;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
